import * as React from 'react'

import { cn } from '../../utils/cn'
import { Logo } from '../brand/Logo'

const HOLD_MS = 700
const EXIT_MS = 500

/**
 * Branded splash. Self-dismisses after the drop animation unless `persist` is
 * set, which holds it until the caller navigates away. Pass `label` when the
 * splash is standing in for real work, so it is announced rather than silent.
 */
export function PageLoader({ persist = false, label }: { persist?: boolean; label?: string }) {
  const [phase, setPhase] = React.useState<'in' | 'leaving' | 'done'>('in')

  React.useEffect(() => {
    if (persist) return
    const leaveTimer = setTimeout(() => setPhase('leaving'), HOLD_MS)
    const doneTimer = setTimeout(() => setPhase('done'), HOLD_MS + EXIT_MS)
    return () => {
      clearTimeout(leaveTimer)
      clearTimeout(doneTimer)
    }
  }, [persist])

  if (phase === 'done') return null

  return (
    <div
      className={cn(
        'bg-ink-50 fixed inset-0 z-10000 flex items-center justify-center opacity-100 transition-opacity duration-500 ease-in',
        phase === 'leaving' && 'pointer-events-none opacity-0'
      )}
      aria-hidden={label ? undefined : 'true'}
      role={label ? 'status' : undefined}
      aria-live={label ? 'polite' : undefined}
    >
      {label && <span className="sr-only">{label}</span>}
      {/* Keyframes ship with the component — marketing.css only loads under MarketingLayout. */}
      <style>
        {`
          @keyframes page-loader-drop {
            0% { opacity: 0; transform: translateY(-32px) scale(0.9) rotate(0deg); }
            55% { opacity: 1; transform: translateY(6px) scale(1.03) rotate(0deg); }
            100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); }
          }
          @keyframes page-loader-spin-out {
            0% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); }
            100% { opacity: 0; transform: translateY(0) scale(1) rotate(360deg); }
          }
        `}
      </style>
      <div
        aria-hidden="true"
        className={cn(
          '[&_path]:fill-ink-400 opacity-0',
          phase === 'leaving'
            ? 'animate-[page-loader-spin-out_0.5s_ease-in_forwards]'
            : 'animate-[page-loader-drop_0.7s_cubic-bezier(0.34,1.56,0.64,1)_forwards]'
        )}
      >
        <Logo symbolOnly width={56} />
      </div>
    </div>
  )
}
